import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Skeleton, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getTripDistanceReconciliationV2 } from 'store/tripSliceV2';
import buildDistanceViewModel from './distance/buildDistanceViewModel';
import ReconciliationTable from './distance/ReconciliationTable';
import GpsHealthTimeline from './distance/GpsHealthTimeline';
import StoppageBreakdown from './distance/StoppageBreakdown';
import OdometerReadingsCard from './distance/OdometerReadingsCard';
import { POLL_INTERVAL_MS, USE_RECONCILIATION_API } from './distance/constants';

const { Title, Text } = Typography;

/**
 * The reconciliation handler parses trip_id with a bare `int()`
 * (tracking_controller.py:379) and 422s on anything else — unlike the rest of the V2 API,
 * which runs ids through `coerce_id` and so accepts the `IRPL-4154` display form the
 * router puts in the URL. Send it a number whatever the route param looks like.
 */
const toNumericTripId = (value) => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	const digits = String(value).match(/(\d+)\s*$/);
	return digits ? Number(digits[1]) : null;
};

/**
 * Trip Overview → Distance.
 *
 * Shown for every trip type (regular, adhoc, intercity) in every state — it never hides,
 * it degrades. Two data sources feed one view model:
 *
 *   - `all_tracking_info`, already fetched and polled by the page, gives planned / GPS /
 *     odometer / final distance, the threshold check and node dwell. That is enough for the
 *     reconciliation card and the source tag, which is why the tab is useful before any
 *     backend work lands.
 *   - `trip_distance_reconciliation` adds GPS coverage, the health timeline and the full
 *     halt breakdown. It does not exist yet, so it sits behind USE_RECONCILIATION_API and
 *     nothing calls it until that flag is flipped.
 */
function DistanceTab({ tripId, trackingInfo, trackingLoading, tripDetails }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [reconciliation, setReconciliation] = useState(null);
	const [reconciliationError, setReconciliationError] = useState(null);

	const statusId = trackingInfo?.trip_info?.trip_status_id;
	const isTripOpen = statusId !== 3 && statusId !== 9;

	// Prefer the id the API itself reported; the route param is the display form.
	const numericTripId = toNumericTripId(trackingInfo?.trip_info?.trip_id ?? tripId);

	const fetchReconciliation = useCallback(
		(onDone) =>
			dispatch(getTripDistanceReconciliationV2({ trip_id: numericTripId }))
				.then((res) => onDone(res, null))
				.catch((err) => onDone(null, err)),
		[dispatch, numericTripId]
	);

	useEffect(() => {
		if (!USE_RECONCILIATION_API || !numericTripId) return undefined;

		let cancelled = false;
		const load = () =>
			fetchReconciliation((res, err) => {
				if (cancelled) return;
				if (err) {
					console.error('Failed to fetch distance reconciliation', err);
					// Keep the last good payload: a transient failure must not blank the tab.
					setReconciliationError(err);
					return;
				}
				setReconciliation(res);
				setReconciliationError(null);
			});

		load();
		// A closed trip's numbers are final — polling one is pure waste.
		const intervalId = isTripOpen ? setInterval(load, POLL_INTERVAL_MS) : null;

		return () => {
			cancelled = true;
			if (intervalId) clearInterval(intervalId);
		};
	}, [fetchReconciliation, numericTripId, isTripOpen]);

	const model = useMemo(
		() => buildDistanceViewModel({ trackingInfo, tripDetails, reconciliation }),
		[trackingInfo, tripDetails, reconciliation]
	);

	if (trackingLoading && !trackingInfo) {
		return (
			<div style={{ padding: 24 }}>
				<Skeleton active paragraph={{ rows: 3 }} />
				<Skeleton active paragraph={{ rows: 5 }} style={{ marginTop: 24 }} />
			</div>
		);
	}

	if (!trackingInfo) {
		return (
			<Alert
				type="error"
				showIcon
				message="Distance details unavailable"
				description="Tracking information for this trip could not be loaded."
				style={{ margin: 24 }}
			/>
		);
	}

	// The reconciliation API's display_id is the identifier users quote ("IRPL-4155"),
	// generated by Postgres as 'IRPL-' || id so it can never drift from the trip. Prefer
	// it; the rest of the chain is the pre-existing fallback for when that call has not
	// landed yet, or failed, and would otherwise render a bare number.
	const displayTripId =
		reconciliation?.display_id ||
		trackingInfo?.trip_info?.trip_id ||
		trackingInfo?.trip_info?.id ||
		tripId;

	return (
		<div style={{ padding: '8px 0 24px 0' }}>
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 24 }}>
				<ArrowLeftOutlined
					onClick={() => navigate(-1)}
					style={{ fontSize: 18, color: '#64748b', cursor: 'pointer', marginTop: 8 }}
				/>
				<div>
					<Title level={3} style={{ margin: 0, fontWeight: 700 }}>
						Distance &amp; GPS Tracking{displayTripId ? ` — ${displayTripId}` : ''}
					</Title>
					<Text style={{ fontSize: 14, color: '#64748b' }}>
						Everything about how this trip&apos;s distance was measured, verified, and
						billed.
					</Text>
				</div>
			</div>

			{model.legacyTrip && (
				<Alert
					type="info"
					showIcon
					message="GPS reconciliation not available for this trip"
					description="This trip closed before distance reconciliation was introduced, so no GPS coverage, health timeline or halt detail was ever captured for it. The distances below come from the trip record itself and are final."
					style={{ marginBottom: 20 }}
				/>
			)}

			{model.isValid === false && (
				<Alert
					type="warning"
					showIcon
					message="This trip is marked invalid — these distances are not billed."
					style={{ marginBottom: 20 }}
				/>
			)}

			{reconciliationError && (
				<Alert
					type="warning"
					showIcon
					message="Showing the last known reconciliation — refreshing it failed."
					style={{ marginBottom: 20 }}
				/>
			)}

			{model.isCrossVendor && model.vehicleVendorName && (
				<Alert
					type="info"
					showIcon
					message={`Cross-vendor trip: the vehicle belongs to ${model.vehicleVendorName}, so GPS health describes that vendor's vehicle while billing stays on the trip's own vendor.`}
					style={{ marginBottom: 20 }}
				/>
			)}

			<ReconciliationTable model={model} />
			<GpsHealthTimeline
				model={model}
				tripId={tripId}
				trackingNodes={trackingInfo?.tracking_nodes}
				trackingInfo={trackingInfo}
			/>
			<StoppageBreakdown model={model} />
			<OdometerReadingsCard model={model} />
		</div>
	);
}

DistanceTab.propTypes = {
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	trackingInfo: PropTypes.object,
	trackingLoading: PropTypes.bool,
	tripDetails: PropTypes.object,
};

export default DistanceTab;
